import { Injectable } from '@angular/core';
import { DVCVariableSet, initializeDevCycle } from '@devcycle/js-client-sdk';
import { environment } from '@environments/environment';
import { PermissionsService } from '@zonar-ui/auth';
import { of, ReplaySubject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { isBoolean } from '@app/modules/shared/utilities/utilities';
import { DataDogService } from '@app/services/data-dog.service';

@Injectable({
  providedIn: 'root'
})
export class FeatureToggleService {
  private devCycleClient;
  private variables: DVCVariableSet;
  private variablesInitialized = false;
  public variableSubject: ReplaySubject<DVCVariableSet> = new ReplaySubject();
  constructor(private permissions: PermissionsService, private datadog: DataDogService) {}

  initializeDevCycle(companyId) {
    this.permissions.getUser().subscribe(user => {
      const dvcUser = {
        isAnonymous: false,
        user_id: user.id,
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
        privateCustomData: {
          userId: user.id,
          emailAddress: user.email,
          companyId
        }
      };
      this.devCycleClient = initializeDevCycle(environment.devCycle.clientId, dvcUser);
      this.devCycleClient.onClientInitialized().then(client => {
        this.variables = client.allVariables();
        this.variablesInitialized = true;
        this.addFeatureFlagsToDD(this.variables);
        this.variableSubject.next(client.allVariables());
      });

      // https://docs.devcycle.com/docs/sdk/client-side-sdks/javascript#subscribing-to-sdk-events
      this.devCycleClient.subscribe('variableUpdated:*', (key, val) => {
        if (this.variablesInitialized) {
          this.datadog.addFeatureFlagEvaluation(key, val.value ?? false);
          this.variables[key] = val;
          this.variableSubject.next(this.variables);
        }
      });
    });
  }

  addFeatureFlagsToDD(vars: DVCVariableSet): void {
    const keys = Object.keys(vars);
    for (const key of keys) {
      this.datadog.addFeatureFlagEvaluation(key, vars[key].value ?? false);
    }
  }

  // this returns the value of the DevCycle variable
  // which could be type: Boolean, String, Number, JSON
  // see https://docs.devcycle.com/docs/home/feature-management/features-and-variables/creating-variables-and-variations
  valueForKey(key: string) {
    return this.variableSubject.pipe(map(variables => variables[key]?.value));
  }

  // this returns a boolean Observable & should be used in templates to determine if the feature is enabled
  isFeatureEnabled(key: string) {
    return this.variableSubject.pipe(
      switchMap(variables => {
        const keyVal = variables[key]?.value;
        if (isBoolean(keyVal)) {
          return of(keyVal);
          // later on we could add other logic here, if value was a number or string or some object
          // but for now we only have booleans, so the no-op is to return false
        } else {
          return of(false);
        }
      })
    );
  }
}
